/**
 * Universal Widget Registry
 *
 * Central registry of all widget types that can be rendered by UniversalRULForm.
 * Backend can use ANY of these widget types in form definitions.
 *
 * To add a new widget type:
 * 1. Create widget component in this directory
 * 2. Add to WIDGET_REGISTRY below
 * 3. Backend can now use it!
 */

import React from 'react';
import { Alert } from 'antd';

// Import existing widget components
import { TextInputWidgetComponent } from './TextInput';
import { EmailInputWidgetComponent } from './EmailInput';
import { PasswordInputWidgetComponent } from './PasswordInput';
import { NumberInputWidgetComponent } from './NumberInput';
import { SelectWidgetComponent } from './Select';
import { CheckboxWidgetComponent } from './Checkbox';
// TextareaWidgetComponent removed - using SimpleTextareaWidget
import { DatePickerWidgetComponent } from './DatePicker';
// LabelWidgetComponent removed - using SimpleLabelWidget for both formats
import { DividerWidgetComponent } from './Divider';
import { ButtonWidgetComponent } from './Button';

// Import new UI widgets (we'll create these)
import { LogoWidget } from './Logo';
import { LanguageSelectorWidget } from './LanguageSelector';
import { ThemeToggleWidget } from './ThemeToggle';
import { LinkWidget } from './Link';

// Simple widgets for formsexamples flat format
import { SimpleTextWidget } from './simple/SimpleText';
import { SimpleEmailWidget } from './simple/SimpleEmail';
import { SimplePasswordWidget } from './simple/SimplePassword';
import { SimpleNumberWidget } from './simple/SimpleNumber';
import { SimpleTextareaWidget } from './simple/SimpleTextarea';
import { SimpleUrlWidget } from './simple/SimpleUrl';
import { SimpleTelWidget } from './simple/SimpleTel';
import { SimpleLabelWidget } from './simple/SimpleLabel';
import { SimpleSwitchWidget } from './simple/SimpleSwitch';
import { SimpleDateWidget } from './simple/SimpleDate';
import { SimpleTimeWidget } from './simple/SimpleTime';
import { SimpleDateTimeWidget } from './simple/SimpleDateTime';
import { SimpleDropdownWidget } from './simple/SimpleDropdown';
import { SimpleFlexLayout } from './simple/SimpleFlexLayout';
import { SimpleInfoPanel } from './simple/SimpleInfoPanel';

/**
 * Widget component interface
 */
export interface WidgetProps {
  widget: any;
  onChange?: (name: string, value: any) => void;
  onBlur?: (name: string, value: any) => void; // For blur patch trigger
  error?: string;
  warning?: string;
  onButtonClick?: () => void;
  // Layout props for container widgets like flex
  formData?: Record<string, any>;
  allErrors?: Record<string, string>;
  allWarnings?: Record<string, string>;
  onFieldChange?: (name: string, value: any) => void;
}

/**
 * Layout props passed to container widgets
 */
export interface LayoutProps {
  formData?: Record<string, any>;
  allErrors?: Record<string, string>;
  allWarnings?: Record<string, string>;
}

/**
 * Central widget registry
 *
 * Maps widget type string to React component.
 * Backend uses these type names in form definitions.
 */
export const WIDGET_REGISTRY: Record<string, React.FC<WidgetProps>> = {
  // Standard RUL widgets (hierarchical format)
  text_input: TextInputWidgetComponent,
  email_input: EmailInputWidgetComponent,
  password_input: PasswordInputWidgetComponent,
  phone_input: TextInputWidgetComponent, // Uses text input with type="tel"
  number_input: NumberInputWidgetComponent,
  select: SelectWidgetComponent,
  checkbox: CheckboxWidgetComponent,
  // textarea moved to flat format section (SimpleTextareaWidget)
  date_picker: DatePickerWidgetComponent,
  label: SimpleLabelWidget, // Use SimpleLabelWidget - handles both formats and css_style
  divider: DividerWidgetComponent,
  button: ButtonWidgetComponent,

  // FormsExamples simple widgets (flat format)
  text: SimpleTextWidget,
  email: SimpleEmailWidget,
  password: SimplePasswordWidget,
  number: SimpleNumberWidget,
  textarea: SimpleTextareaWidget,
  url: SimpleUrlWidget,
  tel: SimpleTelWidget,
  switch: SimpleSwitchWidget,
  date: SimpleDateWidget,
  time: SimpleTimeWidget,
  datetime: SimpleDateTimeWidget,
  dropdown: SimpleDropdownWidget,

  // Layout widgets
  flex: SimpleFlexLayout,

  // Info widgets (need formData)
  info_panel: SimpleInfoPanel,

  // New UI widgets
  logo: LogoWidget,
  language_selector: LanguageSelectorWidget,
  theme_toggle: ThemeToggleWidget,
  link: LinkWidget,
};

/**
 * Render a widget based on its type
 *
 * @param widget - Widget definition from backend
 * @param onChange - Change handler
 * @param error - Validation error (blocks submit, shown in red)
 * @param warning - Validation warning (doesn't block submit, shown in orange)
 * @param onButtonClick - Button click handler
 * @param layoutProps - Props for layout widgets (formData, allErrors, allWarnings)
 * @param onBlur - Blur handler for blur patch trigger
 * @returns Rendered widget component
 */
export const renderWidget = (
  widget: any,
  onChange?: (name: string, value: any) => void,
  error?: string,
  warning?: string,
  onButtonClick?: () => void,
  layoutProps?: LayoutProps,
  onBlur?: (name: string, value: any) => void
): React.ReactNode => {
  const Component = WIDGET_REGISTRY[widget.type];

  if (!Component) {
    return (
      <Alert
        type="warning"
        message="Unknown Widget Type"
        description={`Widget type "${widget.type}" is not registered. Please add it to the widget registry.`}
        showIcon
        style={{ marginBottom: '1rem' }}
      />
    );
  }

  // For layout/container widgets that need formData, pass additional props
  const needsFormData = ['flex', 'grid', 'stack', 'info_panel'].includes(
    widget.type
  );

  return (
    <Component
      key={widget.name || widget.type}
      widget={widget}
      onChange={onChange}
      onBlur={onBlur}
      error={error}
      warning={warning}
      onButtonClick={onButtonClick}
      {...(needsFormData && layoutProps
        ? {
            formData: layoutProps.formData,
            allErrors: layoutProps.allErrors,
            allWarnings: layoutProps.allWarnings,
            onFieldChange: onChange,
          }
        : {})}
    />
  );
};
